import * as React from 'react'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { styled } from '@mui/material/styles'
import { Grid, Box, Button, Stack } from '@mui/material'
import { Typography as MIUTypography } from '@mui/material'
import { InputAdornment, MenuItem } from '@mui/material'
import { schema } from '../../utils/schemaValidation'
import { isUserVerify, getUserById, approveUserAccount } from '../../services'
import { useParams } from 'react-router-dom'
import { AlertContext } from '../../context/AlertContext'

import { AccountCircle } from '@mui/icons-material'
import Textfield from '../../components/FormsUI/Textfield'
import _ from 'lodash'

const Typography = styled(MIUTypography)(({ theme, istitle }) => ({
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1.125em',
  fontWeight: istitle ? 700 : 450,
  lineHeight: '24px',
  color: istitle ? theme.palette.text.primary : theme.palette.text.secondary,
  textAlign: 'center',
}))

const ButtonCustom = styled(Button)(({ theme }) => ({
  height: '40px',
  backgroundColor: theme.palette.secondary.main,
  textTransform: 'capitalize',
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '22px',
  color: theme.palette.common.white,
  fontSize: '16px',
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}))

const styleStack = {
  justifyContent: 'center',
  direction: 'column',
  alignItems: 'center',
  py: 2,
  px: '8vw',
  height: '100%',
  spacing: 2,
  width: '100%',
}

const boxStyle = {
  minWidth: '300px',
  maxWidth: '500px',
  bgcolor: 'background.paper',
  boxShadow: 14,
  pb: '50px',
  px: '25px',
  pt: 6,
  textAlign: 'center',
  borderRadius: '14px',
}

const INITIAL_STATE = {
  username: '',
  lastname: '',
  email: '',
  phone_number_join: '',
  type_requested: '',
  role: '',
}

const FORM_VALIDATION = Yup.object().shape({
  ...schema.USERNAME,
  ...schema.LASTNAME,
  ...schema.EMAIL,
  ...schema.PHONE_JOIN,
  ...schema.ROLE,
})

const roles = [
  { label: 'Investigador', value: 'investigador' },
  { label: 'Visitante', value: 'visitante' },
]

const ValidationAccount = () => {
  const { token } = useParams()

  const { alertRef } = React.useContext(AlertContext)

  const [validToken, setValidToken] = React.useState(true)
  const [stateAccount, setStateAccount] = React.useState(null)
  const [saved, setSaved] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [INITIAL_FORM_STATE, SET_INITIAL_FORM_STATE] = React.useState(INITIAL_STATE)

  const formatToken = token ? _.split(token, '=')?.[1] : ''
  const TOKEN = 'Bearer ' + formatToken

  const handleAuthorizeAccount = async ({ role }) => {
    const response = await approveUserAccount({ role }, TOKEN)

    if (!response?.update) {
      alertRef?.current?.message(response?.message ?? 'No fue posible habilitar la cuenta.')
      alertRef?.current?.isSeverity('warning')
      alertRef?.current?.openAlert(true)
      return
    }

    setSaved(true)
    setStateAccount('aprobado')
  }

  React.useEffect(() => {
    isUserVerify(TOKEN)
      .then(data => {
        if (!data?.auth) {
          setValidToken(false)
          return
        }

        const { user } = data
        const { userIdCheck } = user || {}

        if (Boolean(userIdCheck)) {
          getUserById(userIdCheck, TOKEN)
            .then(res => {
              const user = res.user
              let message = ''
              let severity = ''

              if (res.error) {
                message = 'No fue posible validar la cuenta, intentelo más tarde.'
                severity = 'error'
              } else if (res?.auth) {
                const state = user.estado_solicitud
                message = `Cuenta ${state}`
                severity = state === 'aprobado' ? 'success' : 'info'
                setStateAccount(state)
                SET_INITIAL_FORM_STATE({
                  username: res.user.nombre,
                  lastname: res.user.apellido,
                  email: res.user.email,
                  phone_number_join: res.user.celular,
                  type_requested: res.user.solicitud?.nombre ?? '',
                  role: res.user.rol?.tipo ?? '',
                })
              }
              alertRef?.current?.message(message)
              alertRef?.current?.isSeverity(severity)
              alertRef?.current?.openAlert(true)
            })
            .catch(err => console.error('Error: ', err.message))
        } else setValidToken(false)
      })
      .catch(err => console.error('Error: ', err))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) return null

  if (!stateAccount && !saved && !validToken)
    return (
      <Box sx={boxStyle} mt={'5vh'}>
        <Typography sx={{ fontWeight: 500 }}>
          Este enlace ya no es válido. Es posible que haya caducado el token actual o no
          sea el correcto. Para gestionar cuentas de usuario hágalo en la sección de la
          aplicación web.
        </Typography>
      </Box>
    )

  if (!stateAccount && !saved && validToken)
    return (
      <Box sx={boxStyle} mt={'5vh'}>
        <Typography sx={{ fontWeight: 500 }}>
          No fue posible obtener información del enlace proporcionado. Intentalo
          nuevamente o más tarde.
        </Typography>
      </Box>
    )

  if (stateAccount === 'aprobado')
    return (
      <Box sx={boxStyle} mt={'5vh'}>
        <Typography sx={{ fontWeight: 500 }}>
          El usuario{' '}
          <strong>
            {INITIAL_FORM_STATE.username} {INITIAL_FORM_STATE.lastname}
          </strong>{' '}
          <strong>({INITIAL_FORM_STATE.email})</strong> ha sido aprobado exitosamente.
          La solicitud correspondía a{' '}
          <strong>{INITIAL_FORM_STATE.type_requested}</strong>.
        </Typography>
      </Box>
    )

  return (
    <Box>
      <Stack {...styleStack}>
        <Box sx={boxStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 4 }}>
            <Typography istitle="true">
              Cuenta {stateAccount}
            </Typography>
          </Box>
          <Formik
            initialValues={{ ...INITIAL_FORM_STATE }}
            validationSchema={FORM_VALIDATION}
            onSubmit={handleAuthorizeAccount}>
            <Form>
              <Grid container spacing={2} sx={{ pt: 0, width: '100%', ml: 0 }}>
                <Grid item xs={6} style={{ paddingLeft: 0, paddingRight: 4 }}>
                  <Textfield
                    name="username"
                    label="Nombre"
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: 4 }}>
                  <Textfield
                    name="lastname"
                    label="Apellido"
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingLeft: 0 }}>
                  <Textfield
                    name="email"
                    label="Correo electrónico"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingLeft: 0 }}>
                  <Textfield
                    name="phone_number_join"
                    label="Celular"
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingLeft: 0 }}>
                  <Textfield
                    name="type_requested"
                    label="Tipo de solicitud"
                    variant="standard"
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingLeft: 0 }}>
                  <Textfield
                    name="role"
                    select
                    label="Asignar Rol"
                    size="small"
                    fullWidth>
                    {roles.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Textfield>
                </Grid>

                <Grid item xs={12} style={{ paddingLeft: 4 }}>
                  <ButtonCustom variant="contained" fullWidth type="submit">
                    Habilitar usuario
                  </ButtonCustom>
                </Grid>
              </Grid>
            </Form>
          </Formik>
        </Box>
      </Stack>
    </Box>
  )
}

export default ValidationAccount
